/**
 * Unit conversions and display formatting. Knots are canonical.
 *
 * Every `Observation` stores wind in knots, temperature in °C, pressure in inHg, visibility
 * in statute miles and heights in feet, because that is what an airport report uses and an
 * airport report is the panel every other panel sits next to. A station that reports in mph
 * is converted on the way in; a dropzone that prefers mph sees mph on the way out, with
 * knots alongside so a pilot and a jumper reading the same screen see the same number.
 *
 * **Rounding is directional, and that is the point of this file.** An owner's limit is a
 * line ("gusts above 20 kt"). If a 20.04 kt gust were rounded to 20.0 before the limits
 * engine saw it, or a 20.4 kt gust were displayed as "20 kt" next to "past your limit of
 * 20", rounding would have moved a reading across the owner's line in the unsafe
 * direction. So:
 *
 * - a **gust** (a maximum) is rounded **up** at every precision step;
 * - a **lull** (a minimum) is rounded **down**, so a gust spread is never understated;
 * - an **average** (and everything else) is rounded to nearest, half away from zero.
 *
 * Normalisers keep one decimal (`INTERNAL_WIND_DECIMALS`), rounded by role; only the
 * display helpers round to whole units. Because ceiling is monotone, a gust that is above a
 * limit at full precision is still at or above it after both steps (tested).
 */

// ---- Exact conversion factors (defined, not measured) -------------------------------

/** 1 international nautical mile = 1852 m exactly; 1 statute mile = 1609.344 m exactly. */
export const METRES_PER_NAUTICAL_MILE = 1852
export const METRES_PER_STATUTE_MILE = 1609.344
/** 1 international foot = 0.3048 m exactly. */
export const METRES_PER_FOOT = 0.3048
/** 1 inHg = 33.8638866667 hPa (conventional, at 0 °C). 1 hPa = 1 mb. */
export const HPA_PER_INHG = 33.8638866667

/** Precision normalisers store wind at: tenths of a knot. Display rounds further. */
export const INTERNAL_WIND_DECIMALS = 1

// ---- Wind speed ----------------------------------------------------------------------

export type WindUnit = 'kt' | 'mph' | 'kmh' | 'mps'

/** Metres per second in one unit of each wind unit. */
const MPS_PER: Record<WindUnit, number> = {
  kt: METRES_PER_NAUTICAL_MILE / 3600,
  mph: METRES_PER_STATUTE_MILE / 3600,
  kmh: 1000 / 3600,
  mps: 1,
}

/** Convert a wind speed between any two units. No rounding. */
export function convertWind(value: number, from: WindUnit, to: WindUnit): number {
  if (from === to) return value
  return (value * MPS_PER[from]) / MPS_PER[to]
}

export const mphToKt = (mph: number): number => convertWind(mph, 'mph', 'kt')
export const ktToMph = (kt: number): number => convertWind(kt, 'kt', 'mph')
export const kmhToKt = (kmh: number): number => convertWind(kmh, 'kmh', 'kt')
export const ktToKmh = (kt: number): number => convertWind(kt, 'kt', 'kmh')
export const mpsToKt = (mps: number): number => convertWind(mps, 'mps', 'kt')
export const ktToMps = (kt: number): number => convertWind(kt, 'kt', 'mps')

// ---- Temperature ---------------------------------------------------------------------

export const fToC = (f: number): number => ((f - 32) * 5) / 9
export const cToF = (c: number): number => (c * 9) / 5 + 32

// ---- Pressure ------------------------------------------------------------------------

export const hpaToInHg = (hpa: number): number => hpa / HPA_PER_INHG
export const inHgToHpa = (inHg: number): number => inHg * HPA_PER_INHG
/** Millibars and hectopascals are the same unit; the aliases exist so call sites read true. */
export const mbToInHg = hpaToInHg
export const inHgToMb = inHgToHpa

/**
 * Altimeter setting (hPa) from station pressure (hPa) and station elevation (metres).
 *
 * Some stations (Tempest's `obs_st`) report only the pressure at the sensor. An altimeter
 * setting is that pressure reduced to sea level through the standard atmosphere, which is
 * what an airport's altimeter setting is too, so the two panels stay comparable. This is
 * the NWS / Smithsonian formula (NOAA "Altimeter setting" equation; 0.3 hPa is the
 * standard correction between station pressure and the altimeter's reading):
 *
 *   A = (P − 0.3) · (1 + (P0^n · L / T0) · H / (P − 0.3)^n)^(1/n)
 *
 * with P0 = 1013.25 hPa, L = 0.0065 K/m, T0 = 288 K, n = 0.190284.
 */
export function altimeterHpaFromStation(stationHpa: number, elevationM: number): number {
  const n = 0.190284
  const p = stationHpa - 0.3
  return p * Math.pow(1 + ((Math.pow(1013.25, n) * 0.0065) / 288) * (elevationM / Math.pow(p, n)), 1 / n)
}

// ---- Distance ------------------------------------------------------------------------

export const metresToFeet = (m: number): number => m / METRES_PER_FOOT
export const feetToMetres = (ft: number): number => ft * METRES_PER_FOOT
export const metresToStatuteMiles = (m: number): number => m / METRES_PER_STATUTE_MILE
export const statuteMilesToMetres = (sm: number): number => sm * METRES_PER_STATUTE_MILE
/** The Wunderground upload protocol documents `visibility` in nautical miles. */
export const nauticalMilesToStatuteMiles = (nm: number): number =>
  (nm * METRES_PER_NAUTICAL_MILE) / METRES_PER_STATUTE_MILE
export const statuteMilesToNauticalMiles = (sm: number): number =>
  (sm * METRES_PER_STATUTE_MILE) / METRES_PER_NAUTICAL_MILE

// ---- Rounding ------------------------------------------------------------------------

/** Which way a value may be rounded without moving it across a limit unsafely. */
export type RoundingRole = 'average' | 'gust' | 'lull'

/**
 * Binary floating point turns 10 kt into 9.999999999 or 10.000000001. Without a tolerance,
 * "round a gust up" would turn an exact 10 kt into 10.1. Values within this distance of a
 * rounding step are treated as on it.
 */
const STEP_EPSILON = 1e-9

/** Round to `decimals` places in the direction the role allows. */
export function roundForRole(value: number, decimals: number, role: RoundingRole): number {
  const f = Math.pow(10, decimals)
  const scaled = value * f
  let r: number
  if (role === 'gust') r = Math.ceil(scaled - STEP_EPSILON)
  else if (role === 'lull') r = Math.floor(scaled + STEP_EPSILON)
  else r = Math.sign(scaled) * Math.round(Math.abs(scaled) + STEP_EPSILON)
  // Normalise -0 to 0 so a calm reading never prints as "-0".
  return r / f + 0
}

/** Round to `decimals` places, nearest, half away from zero. */
export function roundTo(value: number, decimals: number): number {
  return roundForRole(value, decimals, 'average')
}

/** The internal (stored) precision for a wind value in knots: one decimal, by role. */
export function roundWindKt(kt: number, role: RoundingRole): number {
  return roundForRole(kt, INTERNAL_WIND_DECIMALS, role)
}

// ---- Display -------------------------------------------------------------------------

const WIND_LABEL: Record<WindUnit, string> = { kt: 'kt', mph: 'mph', kmh: 'km/h', mps: 'm/s' }

/** Decimals shown per unit: m/s values are small enough that whole numbers lose too much. */
const WIND_DISPLAY_DECIMALS: Record<WindUnit, number> = { kt: 0, mph: 0, kmh: 0, mps: 1 }

export interface WindDisplayOptions {
  /** Which way rounding may go. Defaults to `'average'`. Pass `'gust'` for a gust. */
  role?: RoundingRole
  /**
   * Show knots alongside a non-knot preference: "18 mph (16 kt)". Defaults to `true`,
   * because the airport panel and every pilot speak knots.
   */
  withKnots?: boolean
}

/** One wind value in a unit, rounded for display by role. `null` → "—" (not measured). */
export function formatWindValue(kt: number | null, unit: WindUnit, role: RoundingRole = 'average'): string {
  if (kt === null || !Number.isFinite(kt)) return '—'
  const v = roundForRole(convertWind(kt, 'kt', unit), WIND_DISPLAY_DECIMALS[unit], role)
  return `${v.toFixed(WIND_DISPLAY_DECIMALS[unit])} ${WIND_LABEL[unit]}`
}

/**
 * Format a wind speed held in knots for a site's unit preference, with knots alongside.
 * `formatWind(15.6, 'mph')` → "18 mph (16 kt)". `null` → "—", never "0".
 */
export function formatWind(kt: number | null, preference: WindUnit, options: WindDisplayOptions = {}): string {
  const role = options.role ?? 'average'
  const withKnots = options.withKnots ?? true
  if (kt === null || !Number.isFinite(kt)) return '—'
  const primary = formatWindValue(kt, preference, role)
  if (preference === 'kt' || !withKnots) return primary
  return `${primary} (${formatWindValue(kt, 'kt', role)})`
}

export type TemperatureUnit = 'C' | 'F'

/** "21 °C" or "70 °F", whole degrees. `null` → "—". */
export function formatTemperature(c: number | null, unit: TemperatureUnit): string {
  if (c === null || !Number.isFinite(c)) return '—'
  const v = roundTo(unit === 'C' ? c : cToF(c), 0)
  return `${v} °${unit}`
}

export type PressureUnit = 'inHg' | 'hPa'

/** "29.92 inHg" or "1013 hPa", the precision each is conventionally quoted to. */
export function formatAltimeter(inHg: number | null, unit: PressureUnit): string {
  if (inHg === null || !Number.isFinite(inHg)) return '—'
  if (unit === 'inHg') return `${roundTo(inHg, 2).toFixed(2)} inHg`
  return `${roundTo(inHgToHpa(inHg), 0)} hPa`
}
